import { OvCard, OvForm, OvFormContext, OvFormInput } from '@components'
import { HELPER_TEXT, LABELS, PLACEHOLDERS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Stack, CircularProgress, InputAdornment, Button, Tooltip } from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2'
import { CheckCircle, TaskAlt, ErrorOutline } from '@mui/icons-material'
import { ISingleClient, ISingleCountry } from '@typings'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { ClientKvkSearch, KvkPrefilledProvider, useKvkPrefilled } from './client.kvk.search'

interface ClientUpdateFormProps {
  showKvkSearch?: boolean
}

const ClientUpdateFormContent = observer(({ showKvkSearch = false }: ClientUpdateFormProps) => {
  const { shared, client } = useStore()
  const { readOnly } = useContext(OvFormContext)
  const { watch, getValues } = useFormContext()
  const { prefilledFields } = useKvkPrefilled()
  const tax = watch('tax')
  const FormInput = OvFormInput<ISingleClient>

  // Reset VAT validation state when VAT number changes
  useEffect(() => {
    client.resetVatValidation()
  }, [tax, client])

  const handleValidateVat = async () => {
    const vatNumber = getValues('tax')
    await client.validateVat(vatNumber)
  }

  // Helper to check if a field is prefilled
  const isFieldPrefilled = (fieldName: string) => {
    return prefilledFields.has(fieldName)
  }

  return (
    <OvForm>
      <OvCard>
        <Stack
          direction="column"
          gap={2}>
          {/* KVK Search - only show in edit mode when not read-only */}
          {showKvkSearch && !readOnly && <ClientKvkSearch />}
          <Grid2
            container
            spacing={2}>
            <Grid2
              xs={12}
              sm={6}>
              <Stack
                direction="column"
                gap={2}>
                <FormInput
                  name="name"
                  label={LABELS.CLIENT_NAME}
                  highlighted={isFieldPrefilled('name')}
                />
                <FormInput
                  name="code"
                  label={LABELS.CLIENT_CODE}
                />
                <FormInput
                  name="email"
                  label={LABELS.EMAIL}
                  placeholder={PLACEHOLDERS.EMAIL}
                  helperText={HELPER_TEXT.EMAIL}
                />
                <FormInput
                  name="street"
                  label={LABELS.STREET}
                  placeholder={PLACEHOLDERS.STREET}
                  highlighted={isFieldPrefilled('street')}
                />
                <FormInput
                  name="house_number"
                  label={LABELS.HOUSE_NUMBER}
                  placeholder={PLACEHOLDERS.HOUSE_NUMBER}
                  highlighted={isFieldPrefilled('house_number')}
                />
                <FormInput
                  name="house_number_addition"
                  label={LABELS.HOUSE_NUMBER_ADDITION}
                  highlighted={isFieldPrefilled('house_number_addition')}
                />
                <FormInput
                  name="postal_code"
                  label={LABELS.POSTAL_CODE}
                  placeholder={PLACEHOLDERS.POSTAL_CODE}
                  helperText={HELPER_TEXT.POSTAL_CODE_NL}
                  highlighted={isFieldPrefilled('postal_code')}
                />
                <FormInput
                  name="city"
                  label={LABELS.CITY}
                  highlighted={isFieldPrefilled('city')}
                />
                <FormInput
                  type="autocomplete"
                  name="country"
                  isOptionEqualToValue={(option: ISingleCountry, val: ISingleCountry) => option.id === val.id}
                  optionLabelKey="name"
                  label={'Land'}
                  options={shared.countries}
                  highlighted={isFieldPrefilled('country')}
                />
              </Stack>
            </Grid2>
            <Grid2
              xs={12}
              sm={6}>
              <Stack
                direction="column"
                gap={2}>
                <FormInput
                  label={LABELS.COC}
                  name="coc"
                  placeholder={PLACEHOLDERS.KVK_NUMBER}
                  helperText={HELPER_TEXT.KVK_NUMBER}
                  highlighted={isFieldPrefilled('coc')}
                />
                <FormInput
                  label={LABELS.TAX_NUMBER}
                  name="tax"
                  placeholder={PLACEHOLDERS.BTW_ID_NL}
                  error={!!client.vatError}
                  helperText={
                    client.vatError ||
                    (client.vatValid
                      ? `BTW-nummer is geldig${client.vatCompanyName ? ` - ${client.vatCompanyName}` : ''}`
                      : 'Klik op de knop om het BTW-nummer te valideren bij het VIES systeem (alleen EU-landen)')
                  }
                  FormHelperTextProps={{
                    sx: {
                      color: client.vatError ? 'error.main' : client.vatValid ? 'success.main' : 'text.secondary',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {client.vatLoading && <CircularProgress size={20} />}
                        {!client.vatLoading && client.vatValid && <CheckCircle color="success" />}
                        {!client.vatLoading && client.vatError && <ErrorOutline color="error" />}
                        {!client.vatLoading && !client.vatValid && !client.vatError && (
                          <Tooltip title="Valideer BTW-nummer bij VIES">
                            <Button
                              size="small"
                              onClick={handleValidateVat}
                              disabled={!watch('tax') || watch('tax').length < 4}
                              sx={{ minWidth: 'auto', px: 1 }}>
                              <TaskAlt />
                            </Button>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Grid2>
          </Grid2>
        </Stack>
      </OvCard>
    </OvForm>
  )
})

// Wrapper component that provides the KVK prefilled context
export const ClientUpdateForm = (props: ClientUpdateFormProps) => {
  return (
    <KvkPrefilledProvider>
      <ClientUpdateFormContent {...props} />
    </KvkPrefilledProvider>
  )
}
